import { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

// Multi-select dropdown component for filtering
function MultiSelectDropdown({
  options,
  selected,
  onChange,
  label,
}: {
  options: string[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  label: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(option)) {
      newSelected.delete(option);
    } else {
      newSelected.add(option);
    }
    onChange(newSelected);
  };

  const selectAll = () => {
    onChange(new Set(options));
  };

  const clearAll = () => {
    onChange(new Set());
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
          selected.size > 0 && selected.size < options.length
            ? 'bg-blue-600/20 border-blue-500 text-blue-400'
            : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        {label}
        {selected.size > 0 && selected.size < options.length && (
          <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
            {selected.size}
          </span>
        )}
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 max-h-80 overflow-hidden flex flex-col">
          {/* Header with select/clear all */}
          <div className="p-2 border-b border-gray-700 flex gap-2">
            <button
              onClick={selectAll}
              className="flex-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
            >
              Select All
            </button>
            <button
              onClick={clearAll}
              className="flex-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
            >
              Clear All
            </button>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1 p-1">
            {options.map(option => (
              <label
                key={option}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-700 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(option)}
                  onChange={() => toggleOption(option)}
                  className="w-4 h-4 rounded border-gray-500 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                />
                <span className="text-sm text-gray-300 truncate">{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface Character {
  characterId: string;
  characterName: string;
  corporationId: string | null;
  corporationName: string | null;
  allianceId: string | null;
  allianceName: string | null;
  syncStatus: 'never' | 'success' | 'failed' | 'in_progress';
  lastSyncAt: string | null;
  itemCount: number | null;
  errorMessage: string | null;
}

interface Corporation {
  corporationId: string;
  corporationName: string;
  syncStatus: 'never' | 'success' | 'failed' | 'in_progress';
  lastSyncAt: string | null;
  itemCount: number | null;
  errorMessage: string | null;
}

interface Asset {
  itemId: number;
  typeId: number;
  typeName: string;
  iconId: number | null;
  quantity: number;
  locationId: number;
  locationType: string;
  locationFlag: string;
  locationName: string | null;
  solarSystemId: number | null;
  solarSystemName: string | null;
  isSingleton: boolean;
  isBlueprintCopy?: boolean;
  volume: number | null;
  marketGroupId: number | null;
  marketGroupName: string | null;
  containerName?: string;
}

// Get asset icon URL - handles blueprints specially
function getAssetIconUrl(asset: Asset): string {
  // Check if this is a blueprint (type name contains "Blueprint")
  const isBlueprint = asset.typeName.toLowerCase().includes('blueprint');
  if (isBlueprint) {
    // Use /bp for originals (isBlueprintCopy is false or null), /bpc for copies
    const suffix = asset.isBlueprintCopy ? 'bpc' : 'bp';
    return `https://images.evetech.net/types/${asset.typeId}/${suffix}?size=32`;
  }
  return `https://images.evetech.net/types/${asset.typeId}/icon?size=32`;
}

interface TreeNode {
  id: string;
  type: 'summary' | 'character' | 'corporation';
  character?: Character;
  corporation?: Corporation;
  expanded: boolean;
}

const ITEMS_PER_PAGE = 50;

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function Assets() {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [corporations, setCorporations] = useState<Corporation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['summary', 'characters', 'corporations']));
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'never' | 'success' | 'failed' | 'in_progress'>('never');
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [selectedFlags, setSelectedFlags] = useState<Set<string>>(new Set());
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshTotal, setRefreshTotal] = useState(0);

  // Summary search state
  const [summarySearchQuery, setSummarySearchQuery] = useState('');
  const [summarySearchInput, setSummarySearchInput] = useState('');
  const [summarySearchResults, setSummarySearchResults] = useState<(Asset & { characterId: string; characterName: string })[]>([]);
  const [summarySearchLoading, setSummarySearchLoading] = useState(false);
  const [summarySelectedFlags, setSummarySelectedFlags] = useState<Set<string>>(new Set());
  const [summaryCurrentPage, setSummaryCurrentPage] = useState(1);

  // Fetch characters and corporations on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [charsResponse, corpsResponse] = await Promise.all([
          api.get('/assets/characters'),
          api.get('/assets/corporations'),
        ]);
        setCharacters(charsResponse.data.characters);
        setCorporations(corpsResponse.data.corporations || []);
        // Select summary by default
        setSelectedNode({ id: 'summary', type: 'summary', expanded: true });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch assets when a character or corporation is selected
  useEffect(() => {
    if (selectedNode?.type === 'character' && selectedNode.character) {
      const fetchAssets = async () => {
        setAssetsLoading(true);
        setAssetsError(null);
        try {
          const response = await api.get(`/assets/character/${selectedNode.character!.characterId}`);
          setAssets(response.data.assets);
          setSyncStatus(response.data.syncStatus || 'never');
          setLastSyncAt(response.data.lastSyncAt);
        } catch (error: any) {
          console.error('Failed to fetch assets:', error);
          setAssetsError(error.response?.data?.message || error.response?.data?.error || 'Failed to fetch assets');
          setAssets([]);
        } finally {
          setAssetsLoading(false);
        }
      };
      fetchAssets();
    } else if (selectedNode?.type === 'corporation' && selectedNode.corporation) {
      const fetchAssets = async () => {
        setAssetsLoading(true);
        setAssetsError(null);
        try {
          const response = await api.get(`/assets/corporation/${selectedNode.corporation!.corporationId}`);
          setAssets(response.data.assets);
          setSyncStatus(response.data.syncStatus || 'never');
          setLastSyncAt(response.data.lastSyncAt);
        } catch (error: any) {
          console.error('Failed to fetch corporation assets:', error);
          setAssetsError(error.response?.data?.message || error.response?.data?.error || 'Failed to fetch corporation assets');
          setAssets([]);
        } finally {
          setAssetsLoading(false);
        }
      };
      fetchAssets();
    } else {
      setAssets([]);
      setSyncStatus('never');
      setLastSyncAt(null);
    }
  }, [selectedNode]);

  // Sync assets for current character
  const handleSync = async () => {
    if (!selectedNode?.character || syncing) return;

    setSyncing(true);
    try {
      await api.post(`/assets/character/${selectedNode.character.characterId}/sync`);
      // Refresh assets after sync
      const response = await api.get(`/assets/character/${selectedNode.character.characterId}`);
      setAssets(response.data.assets);
      setSyncStatus(response.data.syncStatus || 'success');
      setLastSyncAt(response.data.lastSyncAt);
      // Also update the character in the list
      setCharacters(prev => prev.map(c =>
        c.characterId === selectedNode.character!.characterId
          ? { ...c, syncStatus: 'success', lastSyncAt: response.data.lastSyncAt, itemCount: response.data.assets.length }
          : c
      ));
    } catch (error: any) {
      console.error('Failed to sync assets:', error);
      setSyncStatus('failed');
      setAssetsError(error.response?.data?.message || error.response?.data?.error || 'Failed to sync assets');
    } finally {
      setSyncing(false);
    }
  };

  // Sync assets for current corporation
  const handleCorpSync = async () => {
    if (!selectedNode?.corporation || syncing) return;

    setSyncing(true);
    setAssetsError(null);
    try {
      await api.post(`/corporation/sync/assets/${selectedNode.corporation.corporationId}`);
      // Refresh assets after sync
      const response = await api.get(`/assets/corporation/${selectedNode.corporation.corporationId}`);
      setAssets(response.data.assets);
      setSyncStatus(response.data.syncStatus || 'success');
      setLastSyncAt(response.data.lastSyncAt);
      // Also update the corporation in the list
      setCorporations(prev => prev.map(c =>
        c.corporationId === selectedNode.corporation!.corporationId
          ? { ...c, syncStatus: 'success', lastSyncAt: response.data.lastSyncAt, itemCount: response.data.assets.length }
          : c
      ));
    } catch (error: any) {
      console.error('Failed to sync corporation assets:', error);
      setSyncStatus('failed');
      setAssetsError(error.response?.data?.message || error.response?.data?.error || 'Failed to sync corporation assets');
    } finally {
      setSyncing(false);
    }
  };

  // Refresh all characters sequentially
  const handleRefreshAll = async () => {
    if (refreshingAll || characters.length === 0) return;

    setRefreshingAll(true);
    setRefreshProgress(0);
    setRefreshTotal(characters.length);

    for (let i = 0; i < characters.length; i++) {
      const character = characters[i];
      setRefreshProgress(i);

      try {
        await api.post(`/assets/character/${character.characterId}/sync`);
        // Fetch updated character info
        const response = await api.get(`/assets/character/${character.characterId}`);
        // Update the character in the list
        setCharacters(prev => prev.map(c =>
          c.characterId === character.characterId
            ? { ...c, syncStatus: 'success', lastSyncAt: response.data.lastSyncAt, itemCount: response.data.assets.length }
            : c
        ));
      } catch (error: any) {
        console.error(`Failed to sync ${character.characterName}:`, error);
        // Update character with failed status but continue with others
        setCharacters(prev => prev.map(c =>
          c.characterId === character.characterId
            ? { ...c, syncStatus: 'failed', errorMessage: error.response?.data?.message || 'Sync failed' }
            : c
        ));
      }
    }

    setRefreshProgress(characters.length);
    setRefreshingAll(false);
  };

  // Search across all characters and corporations
  const handleSummarySearch = async () => {
    if (!summarySearchInput.trim() || (characters.length === 0 && corporations.length === 0)) return;

    setSummarySearchLoading(true);
    setSummarySearchQuery(summarySearchInput.trim());
    setSummaryCurrentPage(1);
    setSummarySelectedFlags(new Set());

    const allResults: (Asset & { characterId: string; characterName: string })[] = [];
    const query = summarySearchInput.toLowerCase().trim();

    // Search character assets
    for (const character of characters) {
      try {
        const response = await api.get(`/assets/character/${character.characterId}`);
        const characterAssets = response.data.assets as Asset[];

        // Filter assets matching the search query
        const matchingAssets = characterAssets.filter(asset =>
          asset.typeName.toLowerCase().includes(query) ||
          asset.locationName?.toLowerCase().includes(query) ||
          asset.solarSystemName?.toLowerCase().includes(query) ||
          asset.marketGroupName?.toLowerCase().includes(query)
        );

        // Add character info to each result
        for (const asset of matchingAssets) {
          allResults.push({
            ...asset,
            characterId: character.characterId,
            characterName: character.characterName,
          });
        }
      } catch (error) {
        console.error(`Failed to search ${character.characterName}:`, error);
      }
    }

    // Search corporation assets
    for (const corporation of corporations) {
      try {
        const response = await api.get(`/assets/corporation/${corporation.corporationId}`);
        const corporationAssets = response.data.assets as Asset[];

        // Filter assets matching the search query
        const matchingAssets = corporationAssets.filter(asset =>
          asset.typeName.toLowerCase().includes(query) ||
          asset.locationName?.toLowerCase().includes(query) ||
          asset.solarSystemName?.toLowerCase().includes(query) ||
          asset.marketGroupName?.toLowerCase().includes(query)
        );

        // Add corporation info to each result (using corporationId as characterId for display purposes)
        for (const asset of matchingAssets) {
          allResults.push({
            ...asset,
            characterId: corporation.corporationId,
            characterName: `[CORP] ${corporation.corporationName}`,
          });
        }
      } catch (error) {
        console.error(`Failed to search corporation ${corporation.corporationName}:`, error);
      }
    }

    setSummarySearchResults(allResults);
    setSummarySearchLoading(false);
  };

  // Group assets by location
  const assetsByLocation = useMemo(() => {
    const groups = new Map<number, Asset[]>();
    for (const asset of assets) {
      const existing = groups.get(asset.locationId) || [];
      existing.push(asset);
      groups.set(asset.locationId, existing);
    }
    return groups;
  }, [assets]);

  // Calculate summary stats across all characters
  const summaryStats = useMemo(() => {
    // For now, just show character count - real summary would aggregate all assets
    return {
      characterCount: characters.length,
      totalAssets: assets.length,
      totalVolume: assets.reduce((sum, a) => sum + (a.volume || 0) * a.quantity, 0),
    };
  }, [characters, assets]);

  // Calculate oldest last sync time across all characters
  const oldestSyncTime = useMemo(() => {
    const syncTimes = characters
      .filter(c => c.lastSyncAt)
      .map(c => new Date(c.lastSyncAt!).getTime());

    if (syncTimes.length === 0) return null;
    if (syncTimes.length < characters.length) return null; // Some characters never synced

    const oldest = Math.min(...syncTimes);
    return new Date(oldest).toISOString();
  }, [characters]);

  // Get unique location flags for filter dropdown, combining related flags into groups
  const { uniqueFlags, flagMapping } = useMemo(() => {
    const rawFlags = new Set(assets.map(a => a.locationFlag));
    const displayFlags = new Set<string>();
    const mapping = new Map<string, string[]>(); // displayFlag -> [rawFlags]

    // Helper to add a flag to a group
    const addToGroup = (groupName: string, flag: string) => {
      displayFlags.add(groupName);
      const existing = mapping.get(groupName) || [];
      existing.push(flag);
      mapping.set(groupName, existing);
    };

    for (const flag of rawFlags) {
      const lowerFlag = flag.toLowerCase();

      // Combine slot flags and DroneBay into "Fitted"
      if (lowerFlag.includes('slot') || lowerFlag === 'dronebay') {
        addToGroup('Fitted', flag);
      }
      // Combine AutoFit and Unlocked into "Containers"
      else if (lowerFlag === 'autofit' || lowerFlag === 'unlocked') {
        addToGroup('Containers', flag);
      }
      // Keep other flags as-is
      else {
        displayFlags.add(flag);
        mapping.set(flag, [flag]);
      }
    }

    return {
      uniqueFlags: [...displayFlags].sort(),
      flagMapping: mapping,
    };
  }, [assets]);

  // Filter assets by search query and selected flags
  const filteredAssets = useMemo(() => {
    let result = assets;

    // Filter by selected flags (if any selected)
    if (selectedFlags.size > 0) {
      // Expand selected display flags to raw flags
      const rawFlagsToMatch = new Set<string>();
      for (const selectedFlag of selectedFlags) {
        const rawFlags = flagMapping.get(selectedFlag) || [selectedFlag];
        for (const raw of rawFlags) {
          rawFlagsToMatch.add(raw);
        }
      }
      result = result.filter(asset => rawFlagsToMatch.has(asset.locationFlag));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(asset =>
        asset.typeName.toLowerCase().includes(query) ||
        asset.locationFlag.toLowerCase().includes(query) ||
        asset.locationName?.toLowerCase().includes(query) ||
        asset.solarSystemName?.toLowerCase().includes(query) ||
        asset.marketGroupName?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [assets, searchQuery, selectedFlags, flagMapping]);

  // Pagination
  const totalPages = Math.ceil(filteredAssets.length / ITEMS_PER_PAGE);
  const paginatedAssets = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAssets.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAssets, currentPage]);

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedFlags]);

  // Reset search, filters and page when character changes
  useEffect(() => {
    setSearchQuery('');
    setSelectedFlags(new Set());
    setCurrentPage(1);
  }, [selectedNode]);

  // Get unique flags from summary search results with grouping
  const { summaryUniqueFlags, summaryFlagMapping } = useMemo(() => {
    const rawFlags = new Set(summarySearchResults.map(a => a.locationFlag));
    const displayFlags = new Set<string>();
    const mapping = new Map<string, string[]>();

    const addToGroup = (groupName: string, flag: string) => {
      displayFlags.add(groupName);
      const existing = mapping.get(groupName) || [];
      existing.push(flag);
      mapping.set(groupName, existing);
    };

    for (const flag of rawFlags) {
      const lowerFlag = flag.toLowerCase();
      if (lowerFlag.includes('slot') || lowerFlag === 'dronebay') {
        addToGroup('Fitted', flag);
      } else if (lowerFlag === 'autofit' || lowerFlag === 'unlocked') {
        addToGroup('Containers', flag);
      } else {
        displayFlags.add(flag);
        mapping.set(flag, [flag]);
      }
    }

    return {
      summaryUniqueFlags: [...displayFlags].sort(),
      summaryFlagMapping: mapping,
    };
  }, [summarySearchResults]);

  // Filter summary search results by selected flags
  const filteredSummaryResults = useMemo(() => {
    if (summarySelectedFlags.size === 0) return summarySearchResults;

    const rawFlagsToMatch = new Set<string>();
    for (const selectedFlag of summarySelectedFlags) {
      const rawFlags = summaryFlagMapping.get(selectedFlag) || [selectedFlag];
      for (const raw of rawFlags) {
        rawFlagsToMatch.add(raw);
      }
    }
    return summarySearchResults.filter(asset => rawFlagsToMatch.has(asset.locationFlag));
  }, [summarySearchResults, summarySelectedFlags, summaryFlagMapping]);

  // Pagination for summary search results
  const summaryTotalPages = Math.ceil(filteredSummaryResults.length / ITEMS_PER_PAGE);
  const paginatedSummaryResults = useMemo(() => {
    const start = (summaryCurrentPage - 1) * ITEMS_PER_PAGE;
    return filteredSummaryResults.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSummaryResults, summaryCurrentPage]);

  // Reset page when summary filters change
  useEffect(() => {
    setSummaryCurrentPage(1);
  }, [summarySelectedFlags]);

  const toggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleNodeSelect = (node: TreeNode) => {
    setSelectedNode(node);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex h-full">
        {/* Left Panel - Tree */}
        <div className="w-80 bg-gray-800 border-r border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-700">
            <h1 className="text-xl font-bold text-white">Assets</h1>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {/* Summary Node */}
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                selectedNode?.id === 'summary'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
              onClick={() => handleNodeSelect({ id: 'summary', type: 'summary', expanded: true })}
            >
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="font-medium">Summary</span>
            </div>

            {/* Characters */}
            <div className="mt-4">
              <div
                className="flex items-center gap-2 px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-300"
                onClick={() => toggleExpand('characters')}
              >
                <svg
                  className={`w-3 h-3 transition-transform ${expandedNodes.has('characters') ? 'rotate-90' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                Characters ({characters.length})
              </div>

              {expandedNodes.has('characters') && (
                <div className="mt-1 space-y-1">
                  {characters.map(character => (
                    <div
                      key={character.characterId}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        selectedNode?.type === 'character' && selectedNode.character?.characterId === character.characterId
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                      onClick={() => handleNodeSelect({
                        id: `char-${character.characterId}`,
                        type: 'character',
                        character,
                        expanded: true,
                      })}
                    >
                      <img
                        src={`https://images.evetech.net/characters/${character.characterId}/portrait?size=64`}
                        alt={character.characterName}
                        className="w-8 h-8 rounded-lg border border-gray-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{character.characterName}</p>
                        {character.corporationName && (
                          <p className="text-xs text-gray-400 truncate">{character.corporationName}</p>
                        )}
                      </div>
                      {/* Sync status indicator */}
                      <div className={`w-2 h-2 rounded-full ${
                        character.syncStatus === 'success' ? 'bg-green-500' :
                        character.syncStatus === 'failed' ? 'bg-red-500' :
                        character.syncStatus === 'in_progress' ? 'bg-yellow-500 animate-pulse' :
                        'bg-gray-600'
                      }`} title={`Sync: ${character.syncStatus}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Corporations */}
            {corporations.length > 0 && (
              <div className="mt-4">
                <div
                  className="flex items-center gap-2 px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-300"
                  onClick={() => toggleExpand('corporations')}
                >
                  <svg
                    className={`w-3 h-3 transition-transform ${expandedNodes.has('corporations') ? 'rotate-90' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  Corporations ({corporations.length})
                </div>

                {expandedNodes.has('corporations') && (
                  <div className="mt-1 space-y-1">
                    {corporations.map(corporation => (
                      <div
                        key={corporation.corporationId}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          selectedNode?.type === 'corporation' && selectedNode.corporation?.corporationId === corporation.corporationId
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                        onClick={() => handleNodeSelect({
                          id: `corp-${corporation.corporationId}`,
                          type: 'corporation',
                          corporation,
                          expanded: true,
                        })}
                      >
                        <img
                          src={`https://images.evetech.net/corporations/${corporation.corporationId}/logo?size=64`}
                          alt={corporation.corporationName}
                          className="w-8 h-8 rounded-lg border border-gray-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{corporation.corporationName}</p>
                          <p className="text-xs text-gray-400 truncate">
                            {corporation.itemCount !== null ? `${corporation.itemCount.toLocaleString()} items` : 'Not synced'}
                          </p>
                        </div>
                        {/* Sync status indicator */}
                        <div className={`w-2 h-2 rounded-full ${
                          corporation.syncStatus === 'success' ? 'bg-green-500' :
                          corporation.syncStatus === 'failed' ? 'bg-red-500' :
                          corporation.syncStatus === 'in_progress' ? 'bg-yellow-500 animate-pulse' :
                          'bg-gray-600'
                        }`} title={`Sync: ${corporation.syncStatus}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Details */}
        <div className="flex-1 overflow-hidden flex flex-col bg-gray-900">
          {selectedNode?.type === 'summary' ? (
            // Summary View
            <div className="flex-1 p-6 flex flex-col min-h-0">
              <h2 className="text-2xl font-bold text-white mb-6">Assets Summary</h2>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm">Characters</div>
                  <div className="text-2xl font-bold text-white">{summaryStats.characterCount}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm">Last Refreshed</div>
                  <div className="text-2xl font-bold text-white">
                    {oldestSyncTime ? formatTimeAgo(oldestSyncTime) : 'Never'}
                  </div>
                  {!oldestSyncTime && characters.length > 0 && (
                    <div className="text-xs text-gray-500">Some characters never synced</div>
                  )}
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm mb-2">Refresh All</div>
                  {refreshingAll ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span className="text-sm text-gray-300">
                          {refreshProgress + 1}/{refreshTotal}
                        </span>
                        <span className="text-sm text-gray-400 ml-auto">
                          {Math.round(((refreshProgress + 1) / refreshTotal) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${((refreshProgress + 1) / refreshTotal) * 100}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Syncing {characters[refreshProgress]?.characterName || '...'}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleRefreshAll}
                      disabled={characters.length === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh All Characters
                    </button>
                  )}
                </div>
              </div>

              {/* Search Section */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 flex-1 flex flex-col min-h-0">
                <h3 className="text-lg font-semibold text-white mb-4">Search All Characters & Corporations</h3>

                {/* Search Input */}
                <div className="flex gap-3 items-center mb-4">
                  <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search items across all characters and corporations... (Press Enter)"
                      value={summarySearchInput}
                      onChange={(e) => setSummarySearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSummarySearch();
                        }
                      }}
                      disabled={summarySearchLoading || (characters.length === 0 && corporations.length === 0)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 disabled:opacity-50"
                    />
                  </div>
                  <button
                    onClick={handleSummarySearch}
                    disabled={summarySearchLoading || !summarySearchInput.trim() || (characters.length === 0 && corporations.length === 0)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {summarySearchLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Searching...
                      </>
                    ) : (
                      'Search'
                    )}
                  </button>
                </div>

                {/* Results */}
                {summarySearchLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-gray-400">Searching across all characters...</p>
                    </div>
                  </div>
                ) : summarySearchQuery && summarySearchResults.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p>No results found for "{summarySearchQuery}"</p>
                    </div>
                  </div>
                ) : summarySearchResults.length > 0 ? (
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Filter row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {summaryUniqueFlags.length > 0 && (
                          <MultiSelectDropdown
                            options={summaryUniqueFlags}
                            selected={summarySelectedFlags}
                            onChange={setSummarySelectedFlags}
                            label="Flag"
                          />
                        )}
                        <span className="text-sm text-gray-400">
                          Found {filteredSummaryResults.length.toLocaleString()} items
                          {summarySelectedFlags.size > 0 && summarySelectedFlags.size < summaryUniqueFlags.length && (
                            <span> in {summarySelectedFlags.size} flag{summarySelectedFlags.size !== 1 ? 's' : ''}</span>
                          )}
                        </span>
                      </div>
                      {(summarySelectedFlags.size > 0) && (
                        <button
                          onClick={() => setSummarySelectedFlags(new Set())}
                          className="text-sm text-blue-400 hover:text-blue-300"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>

                    {/* Results Table */}
                    <div className="flex-1 overflow-y-auto min-h-0 border border-gray-700 rounded-lg">
                      <table className="w-full">
                        <thead className="bg-gray-700 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Character</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">System</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Location</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Flag</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {paginatedSummaryResults.map((asset, index) => (
                            <tr key={`${asset.characterId}-${asset.itemId}-${index}`} className="hover:bg-gray-700">
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={`https://images.evetech.net/characters/${asset.characterId}/portrait?size=32`}
                                    alt={asset.characterName}
                                    className="w-6 h-6 rounded"
                                  />
                                  <span className="text-sm text-gray-300 truncate max-w-[100px]" title={asset.characterName}>
                                    {asset.characterName}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={getAssetIconUrl(asset)}
                                    alt={asset.typeName}
                                    className="w-6 h-6 rounded"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                  <span className="text-sm text-white">
                                    {asset.typeName}
                                    {asset.isBlueprintCopy && <span className="ml-1 text-xs text-blue-400">(Copy)</span>}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right text-sm font-mono text-white">
                                {asset.quantity.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-400">
                                {asset.solarSystemName || '-'}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-400 max-w-[150px] truncate" title={asset.locationName || undefined}>
                                {asset.locationName || `Unknown (${asset.locationId})`}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-500">
                                {asset.locationFlag}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {summaryTotalPages > 1 && (
                      <div className="pt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-400">
                          Showing {((summaryCurrentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(summaryCurrentPage * ITEMS_PER_PAGE, filteredSummaryResults.length)} of {filteredSummaryResults.length.toLocaleString()} items
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSummaryCurrentPage(1)}
                            disabled={summaryCurrentPage === 1}
                            className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            First
                          </button>
                          <button
                            onClick={() => setSummaryCurrentPage(p => Math.max(1, p - 1))}
                            disabled={summaryCurrentPage === 1}
                            className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <span className="px-3 py-1 text-sm text-white">
                            Page {summaryCurrentPage} of {summaryTotalPages}
                          </span>
                          <button
                            onClick={() => setSummaryCurrentPage(p => Math.min(summaryTotalPages, p + 1))}
                            disabled={summaryCurrentPage === summaryTotalPages}
                            className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setSummaryCurrentPage(summaryTotalPages)}
                            disabled={summaryCurrentPage === summaryTotalPages}
                            className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p>Enter a search term and press Enter to search across all characters and corporations</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : selectedNode?.type === 'character' || selectedNode?.type === 'corporation' ? (
            // Character/Corporation Assets View
            <div className="flex-1 flex flex-col min-h-0">
              {/* Header */}
              <div className="p-4 border-b border-gray-700 flex items-center gap-4">
                {selectedNode.type === 'character' ? (
                  <img
                    src={`https://images.evetech.net/characters/${selectedNode.character!.characterId}/portrait?size=128`}
                    alt={selectedNode.character!.characterName}
                    className="w-12 h-12 rounded-lg border-2 border-blue-500"
                  />
                ) : (
                  <img
                    src={`https://images.evetech.net/corporations/${selectedNode.corporation!.corporationId}/logo?size=128`}
                    alt={selectedNode.corporation!.corporationName}
                    className="w-12 h-12 rounded-lg border-2 border-blue-500"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-white">
                    {selectedNode.type === 'character'
                      ? selectedNode.character!.characterName
                      : selectedNode.corporation!.corporationName}
                  </h2>
                  {selectedNode.type === 'character' && selectedNode.character!.corporationName && (
                    <p className="text-sm text-gray-400">{selectedNode.character!.corporationName}</p>
                  )}
                  {selectedNode.type === 'corporation' && (
                    <p className="text-sm text-gray-400">Corporation Assets</p>
                  )}
                </div>
                {/* Stats in header */}
                {!assetsLoading && !assetsError && assets.length > 0 && (
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-gray-400">Items:</span>{' '}
                      <span className="text-white font-mono">{assets.length.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Locations:</span>{' '}
                      <span className="text-white font-mono">{assetsByLocation.size.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Volume:</span>{' '}
                      <span className="text-white font-mono">
                        {assets.reduce((sum, a) => sum + (a.volume || 0) * a.quantity, 0).toLocaleString()} m³
                      </span>
                    </div>
                  </div>
                )}
                {/* Sync button and status */}
                <div className="flex items-center gap-3">
                  <div className="text-xs text-gray-400">
                    {syncStatus === 'never' ? (
                      <span>Never synced</span>
                    ) : syncStatus === 'in_progress' ? (
                      <span className="text-blue-400">Syncing...</span>
                    ) : syncStatus === 'failed' ? (
                      <span className="text-red-400">Sync failed</span>
                    ) : (
                      <span>Updated {formatTimeAgo(lastSyncAt)}</span>
                    )}
                  </div>
                  <button
                    onClick={selectedNode.type === 'character' ? handleSync : handleCorpSync}
                    disabled={syncing || assetsLoading}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                  >
                    <svg
                      className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {syncing ? 'Syncing...' : 'Sync'}
                  </button>
                </div>
              </div>

              {/* Content */}
              {assetsLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading assets...</p>
                  </div>
                </div>
              ) : assetsError ? (
                <div className="flex-1 p-6">
                  <div className="bg-red-900/30 border border-red-700 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-red-500 font-medium">Failed to load assets</p>
                        <p className="text-red-400 text-sm mt-1">{assetsError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : assets.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    {syncStatus === 'never' ? (
                      <>
                        <p className="mb-4">No assets synced yet</p>
                        <button
                          onClick={selectedNode.type === 'character' ? handleSync : handleCorpSync}
                          disabled={syncing}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                          <svg
                            className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          {syncing ? 'Syncing...' : 'Sync Assets from ESI'}
                        </button>
                      </>
                    ) : (
                      <p>No assets found for this {selectedNode.type === 'character' ? 'character' : 'corporation'}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col min-h-0">
                  {/* Search Bar and Filters */}
                  <div className="p-4 border-b border-gray-700">
                    <div className="flex gap-3 items-center">
                      <div className="relative flex-1">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search assets..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Flag Filter Dropdown */}
                      {uniqueFlags.length > 0 && (
                        <MultiSelectDropdown
                          options={uniqueFlags}
                          selected={selectedFlags}
                          onChange={setSelectedFlags}
                          label="Flag"
                        />
                      )}
                    </div>

                    {/* Filter status */}
                    {(searchQuery || selectedFlags.size > 0) && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
                        <span>
                          Found {filteredAssets.length.toLocaleString()} items
                          {searchQuery && ` matching "${searchQuery}"`}
                          {selectedFlags.size > 0 && selectedFlags.size < uniqueFlags.length && (
                            <span> in {selectedFlags.size} flag{selectedFlags.size !== 1 ? 's' : ''}</span>
                          )}
                        </span>
                        {(searchQuery || selectedFlags.size > 0) && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setSelectedFlags(new Set());
                            }}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Assets Table */}
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <table className="w-full">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">System</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Location</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Flag</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Volume</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {paginatedAssets.map(asset => (
                          <tr key={asset.itemId} className="hover:bg-gray-800">
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <img
                                  src={getAssetIconUrl(asset)}
                                  alt={asset.typeName}
                                  className="w-6 h-6 rounded"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <span className="text-sm text-white">
                                  {asset.typeName}
                                  {asset.isBlueprintCopy && <span className="ml-1 text-xs text-blue-400">(Copy)</span>}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right text-sm font-mono text-white">
                              {asset.quantity.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-400">
                              {asset.solarSystemName || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-400 max-w-[200px] truncate" title={asset.locationName || undefined}>
                              {asset.locationName || `Unknown (${asset.locationId})`}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {asset.locationFlag}
                            </td>
                            <td className="px-4 py-2 text-right text-sm text-gray-400">
                              {asset.volume ? ((asset.volume * asset.quantity).toLocaleString() + ' m³') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-700 flex items-center justify-between">
                      <div className="text-sm text-gray-400">
                        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredAssets.length)} of {filteredAssets.length.toLocaleString()} items
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          First
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <span className="px-3 py-1 text-sm text-white">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // No selection
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Select a node from the tree to view details
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Assets;
